import { Component } from '@theme/component';
import { debounce, formatLiteralSearchQuery, normalizeReferencePatternSearch } from '@theme/utilities';

/**
 * A custom element that allows the user to clean a search input.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchPageInput - The search input element.
 * @extends {Component<Refs>}
 */
class SearchPageInputComponent extends Component {
  requiredRefs = ['searchPageInput'];

  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.refs.searchPageInput.form?.addEventListener('submit', this.#handleSubmit, {
      signal: this.#controller.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the keydown event on the search input and resets the search when
   * empty and Escape is pressed.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown = debounce((event) => {
    const value = this.refs.searchPageInput.value.trim();

    if (event.key === 'Escape' && value === '') {
      this.#submitEmptySearch();
    }
  }, 100);

  #handleSubmit = (event) => {
    event.preventDefault();

    const searchInput = this.refs.searchPageInput;
    const normalizedSearchTerm = normalizeReferencePatternSearch(searchInput.value.trim());
    const form = event.currentTarget;

    if (!(form instanceof HTMLFormElement)) return;

    const searchUrl = new URL(form.action, location.origin);

    for (const [name, value] of new FormData(form)) {
      searchUrl.searchParams.append(name, value);
    }

    searchUrl.searchParams.set('q', this.#formatSearchQuery(normalizedSearchTerm));
    window.location.href = searchUrl.toString();
  };

  #formatSearchQuery(searchTerm) {
    return this.dataset.formatLiteralQuery === 'true' ? formatLiteralSearchQuery(searchTerm) : searchTerm;
  }

  #submitEmptySearch() {
    const searchInput = this.refs.searchPageInput;

    searchInput.focus();
    searchInput.value = '';

    if (this.#isEmptyState()) return;

    searchInput.form?.submit();
  }

  #isEmptyState = () => {
    const url = new URL(window.location.href);
    const queryParam = url.searchParams.get('q') ?? '';

    return queryParam.trim() === '';
  };
}

if (!customElements.get('search-page-input-component')) {
  customElements.define('search-page-input-component', SearchPageInputComponent);
}
